import { useEffect, useMemo, useState } from "react";
import { usePermissions } from "../../../hooks/usePermissions";
import { useOrganization } from "../../../context/OrganizationContext";
import { CAPABILITY } from "../../../helpers/permissions";
import { canAccessMode } from "../../../helpers/screenGuards";
import { canSeeDashboardTasks } from "../../../helpers/dashboard/dashboardTasks";
import { localTodayISO } from "../../../helpers/planning/planningModel";
import { getPayrollWeekBoundsFromSettings } from "../../../helpers/payrollWeek";
import { loadAccountsPayableOpsSummary } from "../../../helpers/opsDashboardSummaries";
import {
  loadCommandEstimates,
  loadCommandFinance,
  loadCommandReceivables,
  loadCommandTasks,
  loadCommandTimeLogs,
} from "../../../helpers/dashboard/commandDashboardData";
import { dayIndex, dayIndexToISO } from "../../../helpers/planning/planningModel";

const IDLE = { status: "idle", data: null };

/** Loads one module independently so a slow/denied source never blanks the board. */
function useModule(enabled, load, deps) {
  const [state, setState] = useState(enabled ? { status: "loading", data: null } : { status: "locked", data: null });

  useEffect(() => {
    if (!enabled) {
      setState({ status: "locked", data: null });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => (prev.status === "ready" ? prev : { status: "loading", data: null }));
    Promise.resolve()
      .then(load)
      .then((data) => {
        if (cancelled) return;
        setState(data == null ? { status: "unavailable", data: null } : { status: "ready", data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error", data: null });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return state || IDLE;
}

/**
 * Every module is gated by the capability its own screen already uses:
 * tasks (PROJECT_TASK_VIEW), estimates (ESTIMATE_VIEW), receivables
 * (ACCOUNTS_RECEIVABLE_VIEW), payables (ACCOUNTS_PAYABLE_VIEW), finance
 * (finance mode), time logs (TIMELOG_VIEW).
 */
export function useCommandDashboardData({ actor, now }) {
  const { can, actor: permActor, orgPolicies, directReportIds } = usePermissions();
  const { settings } = useOrganization();
  const user = actor || permActor;
  const today = useMemo(() => localTodayISO(now || new Date()), [now]);

  const week = useMemo(() => getPayrollWeekBoundsFromSettings(new Date(), settings), [settings]);
  const sinceIso = useMemo(() => {
    const start = dayIndex(week.weekStartStr);
    return start == null ? today : dayIndexToISO(start - 7);
  }, [week.weekStartStr, today]);

  const allow = {
    tasks: canSeeDashboardTasks(user),
    estimates: can(CAPABILITY.ESTIMATE_VIEW),
    receivables: can(CAPABILITY.ACCOUNTS_RECEIVABLE_VIEW),
    payables: can(CAPABILITY.ACCOUNTS_PAYABLE_VIEW),
    finance: canAccessMode(user, "finance", orgPolicies),
    timeLogs: can(CAPABILITY.TIMELOG_VIEW),
    crew: can(CAPABILITY.DASHBOARD_ACTIVE_CREW),
  };

  const userKey = user?.supabase_user_id || user?.id || "";
  const tasks = useModule(allow.tasks, () => loadCommandTasks(user), [userKey]);
  const estimates = useModule(allow.estimates, () => loadCommandEstimates(user), [userKey]);
  const receivables = useModule(allow.receivables, () => loadCommandReceivables(user), [userKey]);
  const payables = useModule(allow.payables, () => loadAccountsPayableOpsSummary(user, settings), [userKey, settings]);
  const finance = useModule(allow.finance, () => loadCommandFinance(user), [userKey]);
  const timeLogs = useModule(allow.timeLogs, () => loadCommandTimeLogs(user, { sinceIso, directReportIds }), [
    userKey,
    sinceIso,
  ]);

  return { today, week, allow, tasks, estimates, receivables, payables, finance, timeLogs };
}
